import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema } from '../schemas';

/**
 * `{ root: true }` because both routes spell `/projects/:projectId/...` in full.
 * The file has exactly two routes and both migrated, so nothing was silently
 * relocated — but the absolute URLs are asserted in
 * `StorageAccess/src/tests/unit/fastify/issues.routes.test.ts` anyway, since a
 * source diff cannot see a prefix change.
 *
 * The project used to arrive in the `project-id` header, which is the CHOSEN
 * project rather than the issue's. A notification card rendering an issue from
 * another project sent the wrong id, so the attachment was fetched from the
 * wrong bucket — and every consumer swallows the failure (`catch { return '' }`
 * in `shared/services/storage.service.ts`), so it rendered as a blank image
 * with no error surfaced anywhere. A path param is the issue's own project.
 */
const attachmentParamsSchema = z.object({
	projectId: requiredStringSchema,
	issueId: requiredStringSchema,
	attachmentId: requiredStringSchema,
});

export const issuesContract = defineContract(
	'issues',
	{
		uploadAttachment: route({
			method: 'POST',
			path: '/projects/:projectId/issues/:issueId/attachments/:attachmentId',
			contentType: 'multipart/form-data',
			params: attachmentParamsSchema,
			body: z.object({
				file: z.file(),
			}),
			response: z.string(),
			operationId: 'uploadIssueAttachment',
			tags: ['Issues Storage'],
			summary: 'Upload an issue attachment',
		}),
		getAttachment: route({
			method: 'GET',
			path: '/projects/:projectId/issues/:issueId/attachments/:attachmentId',
			params: attachmentParamsSchema,
			response: z.any(),
			operationId: 'getIssueAttachment',
			tags: ['Issues Storage'],
			summary: 'Get an issue attachment',
		}),
	},
	{ root: true }
);
